/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { defaultValue } from './SimpleFilterAttribute';

const SimpleFilterAttributeManager: ComponentManagerFn<
  'SimpleFilterAttribute'
> = (platform: Platform) => {
  return {
    ...baseComponentManager<'SimpleFilterAttribute'>(
      platform,
      'SimpleFilterAttribute',
    ),
    type: 'SimpleFilterAttribute',
    name: 'Quick Filter Attribute',
    isChildAllowed: (page, item, childType) => false,
    defaultValue,
    props: {
      attributeCode: {
        label: 'Attribute',
        type: 'ViewAttributeChooser',
        aliasType: 'STORE',
        getAttributeDataTypes: () => ['String', 'Integer', 'Decimal', 'Double'],
        preventMultiEdit: true,
      },
      caseInsensitive: {
        label: 'Case Insensitive',
        type: 'boolean',
      },
    },
    category: 'Data',
    getDisplayText: (item) => `${item.props.attributeCode}`,
    icon: { iconName: 'filter' },
    description: `**Overview**<br/>
The Simple Filter Attribute is a configuration component that defines a specific filtering rule for its parent, the Simple Filter.
While the parent component handles the user interface (the text input box) and the query execution, this component tells the system which data column to search against. You can add multiple attributes to a single filter to create a multi-field search (e.g., searching "John" matches either FirstName OR LastName).

**1. Data Binding (The Rule)**<br/>
The component requires one essential piece of information to function: the target column.
* **Attribute:** Select the data field from the store that this filter should target.
* **Supported Types:** String, Integer, Decimal, Double.
* **Exclusions:** Boolean and Date fields are excluded because they require specialized inputs (checkboxes/date pickers) rather than text-based fuzzy matching.

**2. Match Logic**<br/>
You can refine how the text comparison is handled.
* **Case Insensitive:**
    * **Enabled (True):** "apple" matches "Apple", "APPLE", and "apple". (Recommended for Names, Descriptions).
    * **Disabled (False):** "apple" only matches "apple". (Recommended for exact Codes, IDs, or Case-Sensitive Keys).

**3. Composition Strategy**<br/>
* **Single Attribute:** If you add one attribute (e.g., "OrderNumber"), the filter acts as a specific search box for that field.
* **Multiple Attributes:** If you add multiple attributes (e.g., "OrderNumber", "CustomerName", "City") to the same parent Simple Filter, the logic effectively becomes an OR search.
* **User Experience:** The user types "Chicago", and the system finds records where City is "Chicago" OR Customer Name contains "Chicago".

**4. Integration**<br/>
* **Parent Requirement:** This component allows no placement flexibility. It must be a direct child of a **Simple Filter**. It does not render any UI of its own; it serves purely as logic configuration for the container.`,
  };
};

export default SimpleFilterAttributeManager;
